import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCartPlus, faXmark, faFileLines, faList, faCartShopping, faTrash } from "@fortawesome/pro-regular-svg-icons";
import { toast } from "react-toastify";
import axios from "axios";
import { getLibraryCartItems, setLibraryCartItems } from "./libraryCartUtils.jsx";

export default function LibraryCartBar({
    selectedProducts,
    cartItems = [],
    onViewCartItems,
    onClearSelection,
    onAddedToCart,
    onDeleteSelected,
    navigateToCheckout,
}) {
    const [showQuotationModal, setShowQuotationModal] = useState(false);
    const [quotationLoading, setQuotationLoading] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);

    const hasSelection = selectedProducts.length > 0;
    const hasCartItems = cartItems.length > 0;
    const hasOnRequestProducts = hasSelection
        ? selectedProducts.some((p) => p.price === null || p.price === undefined)
        : false;

    const handleAddToCart = () => {
        const existing = getLibraryCartItems();
        let addedCount = 0;
        let lockedSkipped = 0;

        selectedProducts.forEach((product) => {
            const existingIndex = existing.findIndex((p) => p.id === product.id);
            const hasFixedPrice = product.has_fixed_price === true;

            if (existingIndex >= 0) {
                if (hasFixedPrice) {
                    lockedSkipped++;
                    return;
                }
                existing[existingIndex].quantity =
                    (existing[existingIndex].quantity || existing[existingIndex].default_quantity || 1) +
                    (product.default_quantity || 1);
            } else {
                existing.push({
                    id: product.id,
                    name: product.name,
                    price: product.price,
                    discount_multiplier: product.discount_multiplier ?? 1,
                    ignore_staffel_discount: product.ignore_staffel_discount ?? false,
                    has_fixed_price: hasFixedPrice,
                    quantity: product.default_quantity || 1,
                    default_quantity: product.default_quantity || 1,
                    file: product.file || null,
                    preview_image: product.preview_image || null,
                    settings: product.settings || null,
                    settings_id: product.settings_id || null,
                    model_id: product.model_id || null,
                    dimensions: product.dimensions || null,
                    model_path: product.model_path || null,
                    glb_path: product.glb_path || null,
                    library_labels: product.labels || [],
                });
            }
            addedCount++;
        });

        setLibraryCartItems(existing);

        const goToCheckout = navigateToCheckout;

        if (addedCount > 0) {
            toast.success(
                <div>
                    <span>
                        {addedCount} {addedCount === 1 ? "product" : "products"} added to cart
                    </span>
                    <button
                        className="library-toast-cart-link"
                        onClick={() => {
                            if (goToCheckout) goToCheckout();
                        }}
                    >
                        Go to cart &rarr;
                    </button>
                </div>,
                { autoClose: 5000 }
            );
        }

        if (lockedSkipped > 0) {
            toast.info(
                `${lockedSkipped} fixed-price ${
                    lockedSkipped === 1 ? "product was" : "products were"
                } already in your cart and could not be increased.`,
                { autoClose: 4000 }
            );
        }

        onAddedToCart();
    };

    const handleRequestQuotation = () => {
        const pricedProducts = selectedProducts.filter((p) => p.price !== null && p.price !== undefined);
        if (hasOnRequestProducts && pricedProducts.length > 0) {
            setShowQuotationModal(true);
            return;
        }
        submitQuotation();
    };

    const submitQuotation = async () => {
        setQuotationLoading(true);
        setShowQuotationModal(false);

        try {
            const labelId = window.localStorage.getItem("label_id");
            const baseUrl = window.location.origin + `/api/${labelId}/library`;

            const response = await axios.post(`${baseUrl}/generate-quotation`, {
                products: selectedProducts.map((p) => ({
                    product_id: p.id,
                    quantity: p.default_quantity || 1,
                })),
            });

            const { yo_number, has_on_request } = response.data;

            toast.success(
                <div>
                    <span>
                        Quotation <strong>{yo_number}</strong> created.
                        {has_on_request && " Some items had no available price."}
                    </span>
                </div>,
                { autoClose: 6000 }
            );
            onAddedToCart();
        } catch (err) {
            console.error("Failed to generate quotation:", err);
            toast.error(err.response?.data?.message || "Failed to generate quotation. Please try again.");
        } finally {
            setQuotationLoading(false);
        }
    };

    const handleDeleteSelected = async () => {
        if (!onDeleteSelected || deleteLoading) return;
        setDeleteLoading(true);
        try {
            await onDeleteSelected();
            setShowDeleteModal(false);
        } finally {
            setDeleteLoading(false);
        }
    };

    if (!hasSelection && !hasCartItems) return null;

    return (
        <>
            <div className="library-cart-stack">
                {hasSelection && (
                    <div className="library-selection-bar">
                        <div className="library-cart-bar-info">
                            <span className="library-cart-bar-count">
                                {selectedProducts.length} {selectedProducts.length === 1 ? "product" : "products"}{" "}
                                selected
                            </span>
                            {hasOnRequestProducts && (
                                <span className="library-cart-bar-notice">Contains items without available price</span>
                            )}
                        </div>
                        <div className="library-cart-bar-actions">
                            <button className="library-btn-cancel" onClick={onClearSelection}>
                                <FontAwesomeIcon icon={faXmark} /> Clear
                            </button>
                            <button
                                className="library-btn-delete"
                                onClick={() => setShowDeleteModal(true)}
                                disabled={deleteLoading}
                            >
                                <FontAwesomeIcon icon={faTrash} /> {deleteLoading ? "Deleting..." : "Delete selected"}
                            </button>
                            <button
                                className="library-cart-bar-quotation"
                                onClick={handleRequestQuotation}
                                disabled={quotationLoading}
                            >
                                <FontAwesomeIcon icon={faFileLines} />
                                {quotationLoading ? "Generating..." : "Request quotation"}
                            </button>
                            {!hasOnRequestProducts && (
                                <button className="library-cart-bar-add" onClick={handleAddToCart}>
                                    <FontAwesomeIcon icon={faCartPlus} /> Add to cart
                                </button>
                            )}
                        </div>
                    </div>
                )}

                {hasCartItems && (
                    <div className="library-cart-bar">
                        <div className="library-cart-bar-cart">
                            <span className="library-cart-badge">
                                Cart ({cartItems.length} {cartItems.length === 1 ? "item" : "items"})
                            </span>
                            <div className="library-cart-bar-actions">
                                <button className="library-cart-bar-view" onClick={onViewCartItems}>
                                    <FontAwesomeIcon icon={faList} /> View cart items
                                </button>
                                <button className="library-cart-bar-checkout" onClick={navigateToCheckout}>
                                    <FontAwesomeIcon icon={faCartShopping} /> Go to checkout
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {showQuotationModal && (
                <>
                    <div className="library-modal-overlay" onClick={() => setShowQuotationModal(false)} />
                    <div className="library-modal">
                        <div className="library-modal-header">
                            <h3>Request quotation</h3>
                            <button className="library-slideover-close" onClick={() => setShowQuotationModal(false)}>
                                <FontAwesomeIcon icon={faXmark} size="lg" />
                            </button>
                        </div>
                        <div className="library-modal-body">
                            <p>
                                Your selection contains products without an available price. These items cannot be added
                                to the cart.
                            </p>
                            <p>All selected products will be included in the quotation. You'll receive it by email.</p>
                            <div className="library-modal-actions">
                                <button className="library-btn-cancel" onClick={() => setShowQuotationModal(false)}>
                                    Cancel
                                </button>
                                <button
                                    className="library-btn-save"
                                    onClick={submitQuotation}
                                    disabled={quotationLoading}
                                >
                                    {quotationLoading ? "Generating..." : "Request quotation"}
                                </button>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {showDeleteModal && (
                <>
                    <div className="library-modal-overlay" onClick={() => setShowDeleteModal(false)} />
                    <div className="library-modal">
                        <div className="library-modal-header">
                            <h3>Delete selected products</h3>
                            <button className="library-slideover-close" onClick={() => setShowDeleteModal(false)}>
                                <FontAwesomeIcon icon={faXmark} size="lg" />
                            </button>
                        </div>
                        <div className="library-modal-body">
                            <p>
                                You're about to remove <strong>{selectedProducts.length}</strong>{" "}
                                {selectedProducts.length === 1 ? "product" : "products"} from your library.
                            </p>
                            <p>Deleted products will no longer appear in your library.</p>
                            <div className="library-modal-actions">
                                <button className="library-btn-cancel" onClick={() => setShowDeleteModal(false)}>
                                    Cancel
                                </button>
                                <button className="library-btn-delete" onClick={handleDeleteSelected}>
                                    {deleteLoading ? "Deleting..." : "Yes, delete selected"}
                                </button>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </>
    );
}
